import type { ServiceTag } from '@my/tms-api';
import { AddressInlineEditor } from '@my/ui';
import { Check, ExternalLink, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { COUNTRY_OPTIONS, type AddressDraft } from './locationDraft';

// ── AddressEditorPanel ──────────────────────────────────────────────────────

/**
 * Inline editor body — wraps `<AddressInlineEditor>` with the local
 * Cancel / Save / Delete action buttons. Mirrors the panel pattern
 * used by `PartyAddressesSection`'s additional-addresses card grid;
 * duplicated here (rather than shared) because the two surfaces have
 * slightly different defaults (this one stays open + always-expanded;
 * the party panel sometimes closes back into a card preview).
 */
export interface AddressEditorPanelProps {
  draft: AddressDraft;
  onChange: (next: AddressDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Hidden in `new` mode; shown only when editing an existing address. */
  onDelete?: () => void;
  isBusy: boolean;
  isValid: boolean;
  /** Service-tag library applicable to Locations. Empty = no tags configured. */
  applicableTags: ServiceTag[];
  tagSet: Set<string>;
  onToggleTag: (tag: ServiceTag) => void;
  /**
   * Preview-only mode for party-linked addresses. The form inputs +
   * map render but every editor button is replaced with a single
   * "Open party" external-link action — the row is owned by the
   * party and edits / deletes happen on the Party Details page.
   */
  readOnly?: boolean;
  /** Open the linked party. Required when `readOnly` is true. */
  onOpenParty?: () => void;
  /**
   * Small red italic line shown at the bottom of the panel for
   * read-only rows — explains where the address is actually managed
   * (Organisation Settings page for org-primary rows, the party page
   * for customer / supplier-linked rows) so the operator doesn't sit
   * confused that fields look editable but Save is missing.
   */
  readOnlyHint?: string;
}

export function AddressEditorPanel({
  draft,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
  isValid,
  applicableTags,
  tagSet,
  onToggleTag,
  readOnly = false,
  onOpenParty,
  readOnlyHint,
}: AddressEditorPanelProps) {
  const { t } = useTranslation(['common', 'locations']);

  // Service-tag chip row dropped into the editor's left column (under the
  // address fields). Same chip palette + toggle behaviour as the parties
  // service-tag row, but built locally because `AddressInlineEditor` lives
  // in `@my/ui` and shouldn't depend on the TMS `ServiceTag` type.
  //
  // In readOnly mode the chips render the active set but are disabled —
  // operator can see what's tagged, but tag toggles happen on the Party
  // Details page (since this row is party-owned).
  const tagsSlot = (
    <div className="space-y-1 pt-1">
      <p className="text-xs font-medium text-slate-600">{t('locations:editor.tags')}</p>
      {applicableTags.length === 0 ? (
        <p className="text-[10px] italic text-slate-400">
          {t('locations:editor.noTagsConfigured')}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {applicableTags.map((tag) => {
            const isActive = tagSet.has(tag.name.toLowerCase());
            return (
              <button
                key={tag.id}
                type="button"
                onClick={readOnly ? undefined : () => onToggleTag(tag)}
                disabled={isBusy || readOnly}
                title={tag.description ?? undefined}
                className={tagChipClass(isActive)}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Hide the AddressInlineEditor's own summary line + footer —
          this panel sits inside a table row that already provides
          Cancel / Save / Delete buttons below. The summary line
          would just be visual noise here, and a duplicate Save
          button would be confusing. `mapPosition='right'` puts the
          map next to the form on full-screen widths (md+); on
          narrower viewports it falls back to the stacked layout. */}
      <AddressInlineEditor
        value={draft}
        onChange={onChange}
        countryOptions={COUNTRY_OPTIONS}
        hasCodeField
        hasNameField
        enablePlacesSearch
        showMap
        mapPosition="right"
        tagsSlot={tagsSlot}
        readOnly={readOnly}
        showSummaryInput={false}
        showFooter={false}
      />

      {/* Action row.
          - Edit mode: Delete at the left, Cancel + Save clustered at
            the right via `justify-between` (suite-wide convention).
          - Read-only mode: the left slot holds a small red italic
            hint explaining which page actually owns the address
            (so the operator doesn't sit confused that fields look
            editable but Save is missing); Cancel + Open-party sit on
            the right. Keeping the hint inside the action row (rather
            than on its own line above) keeps the X button at the same
            vertical level it was before the hint was added. */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="min-w-0 flex-1">
          {readOnly ? (
            readOnlyHint && (
              <p className="text-[11px] italic text-red-600">{readOnlyHint}</p>
            )
          ) : (
            onDelete && (
              <button
                type="button"
                aria-label={t('actions.delete')}
                onClick={onDelete}
                disabled={isBusy}
                className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('actions.cancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          {readOnly ? (
            onOpenParty && (
              <button
                type="button"
                aria-label={t('locations:rowActions.openParty', {
                  name: draft.name || draft.fullAddress,
                })}
                onClick={onOpenParty}
                disabled={isBusy}
                className="flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )
          ) : (
            <button
              type="button"
              aria-label={t('actions.save')}
              onClick={onSave}
              disabled={isBusy || !isValid}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Service-tag chip styling — amber palette mirroring the parties editor's
 * tag row so the same tag reads identically across both surfaces. The
 * chip is a button (toggles selection on click) with a disabled-50 state
 * for the in-flight save / delete window.
 */
function tagChipClass(active: boolean): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  return `${base} ${
    active
      ? 'border-amber-600 bg-amber-600 text-white'
      : 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500 hover:text-amber-950'
  }`;
}
